"use client";

import { c as _c } from "react/compiler-runtime";
import { useT } from "@/lib/i18n";
// "Often drafted with" — the cards / relics / potions that show up in the same
// community runs as this entity, from the cached item-pairings job. Reads
// /api/pairings/{kind}/{id}; renders nothing until data arrives or if the item
// has no partners yet. Cards/relics are ranked by synergy (NPMI); potions are
// "commonly seen with" (RNG, ranked by frequency). Each row names both sides so
// the two confidence directions read plainly, and shows the pair win rate.

import { Link } from "@/i18n/navigation";
import { useEffect, useState } from "react";
import CardHover from "@/app/components/CardHover";
import HoverTooltip from "@/app/components/HoverTooltip";
import { cachedFetch } from "@/lib/fetch-cache";
import type { PairingKind as Kind, PairingPartner as Partner, Pairings } from "@/lib/entity-links";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
const TOP = 5;
const pct = (x: number) => `${Math.round((x ?? 0) * 100)}%`;
export default function EntityPairings(t0) {
  const $ = _c(34);
  const {
    kind,
    id,
    name,
    lang,
    bp,
    initial
  } = t0;
  const t = useT();
  const [data, setData] = useState(initial ?? null);
  const [loaded, setLoaded] = useState(initial !== undefined);
  let t1;
  let t2;
  if ($[0] !== id || $[1] !== initial || $[2] !== kind || $[3] !== lang) {
    t1 = () => {
      if (initial !== undefined) {
        return;
      }
      let alive = true;
      cachedFetch(`${API}/api/pairings/${kind}/${id}?lang=${lang}`).then(d => {
        if (alive) {
          setData(d);
          setLoaded(true);
        }
      }).catch(() => {
        if (alive) {
          setLoaded(true);
        }
      });
      return () => {
        alive = false;
      };
    };
    t2 = [kind, id, lang, initial];
    $[0] = id;
    $[1] = initial;
    $[2] = kind;
    $[3] = lang;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[6] !== bp || $[7] !== data?.partners || $[8] !== loaded || $[9] !== name || $[10] !== t) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const p = data?.partners || {};
      const allGroups = [{
        key: "cards",
        heading: t("Cards"),
        items: (p.cards || []).slice(0, TOP)
      }, {
        key: "relics",
        heading: t("Relics"),
        items: (p.relics || []).slice(0, TOP)
      }, {
        key: "potions",
        heading: t("Commonly seen with"),
        items: (p.potions || []).slice(0, TOP)
      }];
      const groups = allGroups.filter(_temp);
      if (!loaded || groups.length === 0) {
        t8 = null;
        break bb0;
      }
      const withHover = (g_0, it) => {
        const link = <Link href={`${bp}/${g_0}/${it.id.toLowerCase()}`} className="pair-name">{it.name}</Link>;
        if (g_0 === "cards") {
          return <CardHover cardId={it.id}>{link}</CardHover>;
        }
        return <HoverTooltip title={it.name} content={it.desc} image={it.image_url}>{link}</HoverTooltip>;
      };
      t5 = "pairings";
      let t9;
      if ($[17] !== t) {
        t9 = t("Often drafted with");
        $[17] = t;
        $[18] = t9;
      } else {
        t9 = $[18];
      }
      if ($[19] !== t9) {
        t6 = <h2>{t9}</h2>;
        $[19] = t9;
        $[20] = t6;
      } else {
        t6 = $[20];
      }
      let t10;
      if ($[21] !== t) {
        t10 = t("How often each shows up in the same community runs as");
        $[21] = t;
        $[22] = t10;
      } else {
        t10 = $[22];
      }
      if ($[23] !== name || $[24] !== t10) {
        t7 = <p className="h-note">{t10} {name}.</p>;
        $[23] = name;
        $[24] = t10;
        $[25] = t7;
      } else {
        t7 = $[25];
      }
      t3 = "pair-groups";
      t4 = groups.map(g_1 => {
        const unit = g_1.key === "potions" ? t("runs") : t("decks");
        const verb = g_1.key === "potions" ? t("also had") : t("also run");
        return <div key={g_1.key} className="pair-group"><h3 className="subh">{g_1.heading}</h3><ul className="pair-list">{g_1.items.map(it_0 => <li key={it_0.id} className="pair-row"><div className="pair-head">{withHover(g_1.key, it_0)}<span className="pair-wr">{pct(it_0.winrate)} {t("win rate together")}</span></div><span className="pair-stats"><span>{pct(it_0.conf)} {t("of")} {name} {unit} {verb} {it_0.name}</span><span>{pct(it_0.conf_rev)} {t("of")} {it_0.name} {unit} {verb}{" "}{name}</span></span></li>)}</ul></div>;
      });
    }
    $[6] = bp;
    $[7] = data?.partners;
    $[8] = loaded;
    $[9] = name;
    $[10] = t;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  let t9;
  if ($[26] !== t3 || $[27] !== t4) {
    t9 = <div className={t3}>{t4}</div>;
    $[26] = t3;
    $[27] = t4;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t5 || $[30] !== t6 || $[31] !== t7 || $[32] !== t9) {
    t10 = <section id={t5}>{t6}{t7}{t9}</section>;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  return t10;
}
function _temp(g) {
  return g.items.length > 0;
}